import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { stagger } from '../motion';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  wide?: boolean;
  children: React.ReactNode;
}

// 開いている板の順。Esc は一番上の板だけを閉じる（重ねて開いたときに下の板まで閉じない）
const openSheets: symbol[] = [];

/** 画面の上に出す板。下からばねで上がる。 */
export const Sheet: React.FC<Props> = ({ open, onClose, title, wide, children }) => {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const me = Symbol('sheet');
    openSheets.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openSheets[openSheets.length - 1] === me) closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      openSheets.splice(openSheets.indexOf(me), 1);
    };
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="v2 v2-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            className={`v2-sheet${wide ? ' v2-sheet--wide' : ''}`}
            role="dialog"
            aria-modal
            aria-label={title}
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          >
            <div className="v2-sheet-head">
              <span className="v2-sheet-title">{title}</span>
              <motion.button className="v2-btn v2-btn--ghost v2-btn--icon" onClick={onClose} aria-label="閉じる" whileTap={{ scale: 0.9, rotate: 90 }}>
                <X size={20} />
              </motion.button>
            </div>
            {/* 中の Section などは親から「出す」指示を受けて現れる。板の中ではここがその親になる */}
            <motion.div className="v2-sheet-body" variants={stagger(0.04)} initial="hidden" animate="show">
              {children}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
